import type { LineItemsColumn, LineItemsColumnKey } from '../types/BlockContent/LineItemsTableContent';
import { defaultContentForKind } from './defaultContentForKind';

/** Internal-only template columns — never render for customer audience / customer PDF. */
export const INTERNAL_LINE_ITEMS_COLUMN_KEYS: ReadonlySet<LineItemsColumnKey> = new Set(['cost', 'margin']);

const CANONICAL_KEYS: ReadonlySet<string> = new Set(['item', 'name', 'description', 'unit', 'quantity', 'rate', 'total', 'cost', 'margin']);

/** Maps legacy / alias wire keys to canonical line-items column keys. */
const KEY_ALIASES: Readonly<Record<string, LineItemsColumnKey>> = {
  /** Legacy wire key — canonical column is `name`. */
  item: 'name',
  name: 'name',
  description: 'description',
  unit: 'unit',
  quantity: 'quantity',
  qty: 'quantity',
  rate: 'rate',
  unit_price: 'rate',
  unitPrice: 'rate',
  total: 'total',
  amount: 'total',
  cost: 'cost',
  cost_per_unit: 'cost',
  costPerUnit: 'cost',
  margin: 'margin',
  margin_percent: 'margin',
  marginPercent: 'margin'
};

export type DocumentAudience = 'internal' | 'customer';

export function normalizeLineItemsColumnKey(raw: string): LineItemsColumnKey {
  const trimmed = raw.trim();
  const mapped = KEY_ALIASES[trimmed];
  if (mapped) {
    return mapped;
  }

  if (CANONICAL_KEYS.has(trimmed)) {
    return trimmed as LineItemsColumnKey;
  }

  return 'name';
}

export function isInternalLineItemsColumn(key: string): boolean {
  return INTERNAL_LINE_ITEMS_COLUMN_KEYS.has(normalizeLineItemsColumnKey(key));
}

export function isLineItemsNumericColumn(key: string): boolean {
  const normalized = normalizeLineItemsColumnKey(key);
  return normalized === 'quantity' || normalized === 'rate' || normalized === 'total' || normalized === 'cost' || normalized === 'margin';
}

function defaultLineItemsColumns(): LineItemsColumn[] {
  const defaults = defaultContentForKind('line_items_table');
  return defaults.kind === 'line_items_table' ? defaults.columns.map((col) => ({ ...col })) : [];
}

/**
 * Ensures every line_items_table block exposes the full column set (including COST / MARGIN %).
 * Legacy blocks without internal columns are upgraded while preserving saved labels and visibility.
 */
export function mergeLineItemsColumnsWithDefaults(columns: LineItemsColumn[]): LineItemsColumn[] {
  const defaults = defaultLineItemsColumns();
  if (defaults.length === 0) {
    return columns;
  }

  const normalized = columns.map((col) => ({
    ...col,
    key: normalizeLineItemsColumnKey(String(col.key))
  }));

  const byKey = new Map(normalized.map((col) => [col.key, col]));
  return defaults
    .map((def) => {
      const existing = byKey.get(def.key);
      if (!existing) {
        return { ...def };
      }

      return {
        key: def.key,
        label: existing.label?.trim() ? existing.label : def.label,
        visible: existing.visible,
        order: def.order
      };
    })
    .sort((a, b) => a.order - b.order);
}

/** Visible columns for the given audience — customer mode always strips internal columns. */
export function filterLineItemsColumnsForAudience(columns: readonly LineItemsColumn[], audience: DocumentAudience): LineItemsColumn[] {
  const visible = columns.filter((col) => col.visible);
  if (audience === 'internal') {
    return [...visible].sort((a, b) => a.order - b.order);
  }

  return visible.filter((col) => !isInternalLineItemsColumn(col.key)).sort((a, b) => a.order - b.order);
}
